import ReactSelect, {
  components,
  Props,
  type GroupBase,
  type OptionProps,
  type SelectComponentsConfig,
  type MenuPlacement,
} from "react-select"

export type { SingleValue, MultiValue } from "react-select"
import "./Select.css"
import { cva } from "class-variance-authority"
import React, { useMemo } from "react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import Label from "../Label"
import { Input } from "../Input"
import cn from "../../../../utils/classes"
import { ZIndex } from "../../../../utils/zIndex"
import { FeatureFlag } from "../../../../domain/enums/featureFlags"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import ColumnContainer from "../Layout/ColumnContainer"
import RowContainer from "../Layout/RowContainer"
import Icon, { type Size } from "../Icon"
import { SENTINEL_OPTION, isSentinelOption } from "./sentinel"

const REVAMP_TOKENS = {
  primaryBlueLight: "#F4FBFD", // tailwind: primary-blue-light
  gray200: "#E5E7EB", // tailwind: gray-200 (default)
} as const

// TODO: Get better at typescript and rewrite the types for CustomDropDownMenu
interface CustomDropDownMenuProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any
  customOption: (props: {
    label: string
    value: typeof Option
    selected: boolean
  }) => React.ReactNode
}

/**
 * Custom dropdown menu component for the Select component.
 * Do not export, internal use only.
 * @param {object} props - The ReactSelect props.
 * @param {(props: {label: string; value: any; selected: boolean}) => JSX.Element} customOption - Closure that returns a custom option component for the select component.
 * @returns {JSX.Element} The custom dropdown menu component.
 */
const CustomDropDownMenu: React.FC<CustomDropDownMenuProps> = ({
  props,
  customOption,
}) => {
  const selectedOptions = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => props.getValue().map((option: any) => option.value),
    [props],
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (option: any) => {
    if (props.isMulti) {
      // If the option is already selected, remove it from the selected options
      if (selectedOptions.includes(option.value)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newValue = props.getValue().filter((selectedOption: any) => {
          return selectedOption.value !== option.value
        })
        props.setValue(newValue)
      } else {
        // If the option is not selected, add it to the selected options
        props.setValue([...props.getValue(), option])
      }
    } else {
      props.setValue(option)
    }
  }

  const filteredOptions = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unselectedOptions = props.options.filter((option: any) => {
      return !selectedOptions.includes(option.value)
    })

    // hide selected options if hideSelectedOptions is true
    if (props.selectProps.hideSelectedOptions) {
      if (props.selectProps.inputValue === "") {
        return unselectedOptions
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return unselectedOptions.filter((option: any) =>
        option.value.includes(props.selectProps.inputValue),
      )
    }
    // show all options if hideSelectedOptions is false and inputValue is empty
    else if (props.selectProps.inputValue === "") {
      return props.options
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return unselectedOptions.filter((option: any) =>
      option.value.includes(props.selectProps.inputValue.toLowerCase()),
    )
  }, [
    props.options,
    props.selectProps.hideSelectedOptions,
    props.selectProps.inputValue,
    selectedOptions,
  ])

  return (
    // TODO: get from CMS
    <components.MenuList
      {...props}
      className="!p-0"
      aria-label="Select options"
      autoFocus
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {filteredOptions.map((option: any) => {
        const highlightBackground = selectedOptions.includes(option.value)
        const selected = selectedOptions.includes(option.value)
        const CustomOption = customOption({ ...option, selected })

        return (
          <components.Option {...props} className="!p-0" key={option.value}>
            <div
              role="option"
              className={cn(
                "border-0 bg-white hover:bg-primary-blue-light text-gray-700 py-2 pl-3 pr-9",
                {
                  "!bg-primary-blue text-white": highlightBackground,
                  "!bg-primary-blue-light":
                    props.focusedOption?.value === option.value,
                },
              )}
              aria-label={option.label}
              tabIndex={-1}
              onClick={() => handleClick(option)}
              aria-selected={selected}
            >
              {CustomOption}
            </div>
          </components.Option>
        )
      })}
    </components.MenuList>
  )
}

export type SelectProps<
  T extends Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<T> = GroupBase<T>,
> = Props<T, IsMulti, Group> & {
  customOption?: (props: {
    label: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any
    selected: boolean
  }) => React.ReactNode
  showLabel?: boolean
  ariaLabel: string
  inputId: string
  label: string | React.ReactNode
  isClearable?: boolean
  isMulti?: boolean
  isRequired?: boolean
  labelPosition?: "top" | "bottom" | "right" | "left"
  size?: "sm" | "md" | "lg"
  styleOverrides?: string
  containerStyleOverrides?: string
  controlStyleOverrides?: string
  valueContainerStyleOverrides?: string
  singleValueStyleOverrides?: string
  menuStyleOverrides?: string
  indicatorsContainerStyleOverrides?: string
  dropdownIndicatorColor?: string
  useDefaultBorderlessIndicator?: boolean
  error?: boolean
  backgroundColor?: string
  errorMessage?: string
  borderless?: boolean
  placeholderTextPosition?: "left" | "right" | "center"
  placeholderTextColor?: "white" | "black"
  iconSize?: Size
  options: readonly T[]
  onChange: (selectedOption: IsMulti extends true ? T[] : T | null) => void
  defaultValue?: T | readonly T[] | null
  /**
   * Marks a single-select as optional under the dropdown revamp (ticket 18848).
   * Takes effect only when the `IsAdvancedSearchRevampEnabled` PostHog flag is on.
   *
   * Under revamp:
   *  - Prepends a "--" sentinel option that, when picked, fires onChange(null).
   *  - Forces isClearable=false (no trigger-level X clear).
   *
   * Caller must use controlled mode (`value`, not `defaultValue`) — the
   * sentinel relies on the parent propagating `null` back as `value={null}`.
   * Ignored when `isMulti` is true.
   */
  isOptionalSelect?: boolean
  menuPlacement?: MenuPlacement
}

/**
 * Select component wrapper around react-select. https://react-select.com/home
 * @param {string} ariaLabel - The ARIA label for the select component. Should come from CMS.
 * @param {string | React.ReactNode} label - The label for the select component. Can pass a custom Label component. Text should come from CMS.
 * @param {OptionProps[]} options - The options for the select component.
 * @param {(selectedOption: { label: string; value: any }) => void} onChange - The callback function to handle the change event.
 * @param {boolean} isClearable - Indicates whether the select component allows clearing the selected option(s).
 * @param {boolean} isMulti - Whether the select component allows multiple selections.
 * @param {boolean} isRequired - Whether the select component is required.
 * @param {boolean} showLabel - Shows label if true.
 * @param {string} placeholder - The placeholder text for the select component. Should come from CMS.
 * @param {string} size - "sm" | "md" | "lg" - The size of the select component.
 * @param {string} styleOverrides - The tw-style overrides for the select component.
 * @param {string} containerStyleOverrides - The tw-style overrides for the select container component.
 * @param {string} controlStyleOverrides - The tw-style overrides for the select control component.
 * @param {string} valueContainerStyleOverrides - The tw-style overrides for the select component.
 * @param {string} singleValueStyleOverrides - The tw-style overrides for a single-value select component.
 * @param {string} menuStyleOverrides - The tw-style overrides for the select menu component.
 * @param {string} indicatorsContainerStyleOverrides - The tw-style overrides for the indicators container component.
 * @param {boolean} error - Indicates whether the select component has an error.
 * @param {string} errorMessage - The error message for the select component. Should come from CMS.
 * @param {string} defaultValue - The initial value for the select component.
 * @param {string} labelPosition - The position of the label relative to the select component.
 * @param {"left" | "center" | "right"} [placeholderTextPosition="left"] - The alignment of the placeholder text. Can be "left", "center", or "right".
 * @param {"white" | "black"} placeholderTextColor="black" - The font color of the placeholder text. Can be "white" or "black".
 * @param {Size} iconSize - The size of the icon. This can be "sm", "md", "ml", "lg", "xl", or "2xl".
 * @param {(props: {label: string; value: any; selected: boolean}) => JSX.Element} customOption - Closure that returns a custom option component for the select component.
 * @returns {JSX.Element} The select component.
 */
export function Select<
  T extends Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<T> = GroupBase<T>,
>(
  props: SelectProps<T, IsMulti, Group> & {
    readOnly?: boolean
    inputType?: string
  },
) {
  const {
    ariaLabel,
    customOption,
    error,
    errorMessage,
    label,
    labelPosition = "top",
    inputId,
    isClearable = true,
    isMulti,
    isRequired = false,
    options,
    placeholder,
    showLabel = true,
    size = "md",
    backgroundColor = "#F9FAFB", //bg-gray-50
    styleOverrides,
    containerStyleOverrides,
    controlStyleOverrides,
    valueContainerStyleOverrides,
    singleValueStyleOverrides,
    menuStyleOverrides,
    indicatorsContainerStyleOverrides,
    dropdownIndicatorColor,
    onChange,
    value,
    defaultValue,
    borderless = false,
    useDefaultBorderlessIndicator = false,
    placeholderTextPosition = "left",
    placeholderTextColor = "black",
    iconSize,
    readOnly = false,
    inputType,
    isOptionalSelect = false,
    menuPlacement = "auto",
    ...rest
  } = props

  const flagEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsAdvancedSearchRevampEnabled,
  )
  const revampOn = flagEnabled === true

  const { cms } = useCMSReady(["common"])
  const sentinelAriaLabel =
    cms.common?.clearSelectionAriaLabel ?? "Clear selection"

  // Optional single-select gets a sentinel "--" option prepended with
  // ability to clear the current selection.
  const shouldPrependSentinel = revampOn && isOptionalSelect && !isMulti
  const effectiveOptions = useMemo(
    () =>
      shouldPrependSentinel
        ? [SENTINEL_OPTION as unknown as T, ...options]
        : options,
    [shouldPrependSentinel, options],
  )

  // Under revamp, the component owns isClearable.
  const effectiveIsClearable = useMemo(
    () => (!revampOn ? isClearable : isMulti),
    [revampOn, isClearable, isMulti],
  )

  const effectiveOnChange = useMemo(() => {
    if (!revampOn) return onChange

    if (!shouldPrependSentinel && !isMulti) return onChange

    return ((selectedOption: unknown) => {
      if (shouldPrependSentinel && isSentinelOption(selectedOption)) {
        ;(onChange as (v: T | null) => void)(null)
        return
      }

      // Ensure "null"/empty return is empty array where onChange expects it.
      if (isMulti && selectedOption == null) {
        ;(onChange as unknown as (v: T[]) => void)([] as T[])
        return
      }
      ;(onChange as (v: unknown) => void)(selectedOption)
    }) as typeof onChange
  }, [revampOn, shouldPrependSentinel, isMulti, onChange])

  const inputVariants = cva(["react-select-input-container", "!m-0"], {
    variants: {
      size: {
        sm: "h-6",
        md: "h-8.5",
        lg: "h-14",
      },
    },
  })

  // TODO (NF): hoist these to other components as needed (button/button wrapper both come to mind first)
  //       will do this in my next PR when I start integrating with affiliate-web
  const focusAndActiveStyle =
    "focus-visible:outline-none focus-visible:!ring-2 focus-visible:ring-[#2198CC] focus-visible:ring-offset-2 focus-visible:ring-offset-white active:outline-none active:ring-2 active:ring-[#2198CC] active:ring-offset-2 active:ring-offset-white"
  const transparentButtonStyle =
    "flex !rounded-md cursor-pointer py-2 px-3 !bg-white !border !border-solid !border-transparent hover:!border-gray-300 hover:!bg-gray-50"
  const controlVariants = cva(
    [
      "ring-1",
      "space-x-1",
      "font-display",
      "!rounded-md",
      "!pl-3",
      "!font-display",
      "!text-sm",
      "!min-h-[42px]",
    ],
    {
      variants: {
        error: {
          true: "!ring-red-500",
        },
        borderless: {
          true: `ring-0 !shadow-0 ${focusAndActiveStyle} ${transparentButtonStyle}`,
        },
        isFocused: {
          true: "ring-primary-blue",
          false: "ring-gray-300",
        },
      },
    },
  )

  const placeholderVariants = cva(
    ["!font-display", "!text-sm", "!whitespace-nowrap", "!text-black"],
    {
      variants: {
        borderless: {
          true: "!text-dark-grey",
        },
        placeholderTextColor: {
          white: "!text-white",
          black: "!text-black",
        },
        placeholderTextPosition: {
          left: "text-left",
          center: "text-center",
          right: "text-right",
        },
      },
    },
  )

  const optionVariants = cva(
    [
      "hover:bg-primary-blue-light",
      "font-display",
      "border-y",
      "border-transparent",
      "hover:border-grey",
      "!font-display",
      "!text-sm",
      "bg-white",
    ],
    {
      variants: {
        isFocused: {
          true: "!bg-primary-blue-light",
        },
        isSelected: {
          true: "!bg-primary-blue hover:border-transparent",
        },
        borderless: {
          true: "whitespace-nowrap",
        },
      },
    },
  )

  const containerVariants = cva([], {
    variants: {
      labelPosition: {
        top: "space-y-2",
        left: "items-center",
        right: "items-center flex-row-reverse",
        bottom: "space-y-1 flex-col-reverse space-x-1",
      },
      borderless: {
        true: "borderless",
      },
    },
  })

  const valueContainerVariants = cva(["!m-0"], {
    variants: {
      borderless: {
        true: "!pl-0",
      },
    },
  })

  const singleValueVariants = cva(["!m-0", "!font-display", "!text-sm"], {
    variants: {
      // .. add here as needed
    },
  })

  const menuContainerVariants = cva([], {
    variants: {
      borderless: {
        true: "min-w-min right-0",
      },
    },
  })

  const indicatorSeperatorVariants = cva(["block", "!bg-gray-500"], {
    variants: {
      borderless: {
        true: "hidden",
      },
    },
  })

  const indicatorsContainerVariants = cva([], {
    variants: {
      borderless: {
        true: "pt-1/2",
      },
    },
  })

  const inputClassNames = useMemo(() => {
    return cn(inputVariants({ size }), styleOverrides)
  }, [inputVariants, size, styleOverrides])

  const containerClassNames = useMemo(() => {
    return cn(
      containerVariants({ labelPosition, borderless }),
      containerStyleOverrides,
    )
  }, [borderless, containerStyleOverrides, containerVariants, labelPosition])

  const placeholderClassNames = useMemo(() => {
    return cn(
      placeholderVariants({
        borderless,
        placeholderTextPosition,
        placeholderTextColor,
      }),
    )
  }, [
    placeholderVariants,
    borderless,
    placeholderTextPosition,
    placeholderTextColor,
  ])

  const valueContainerClassNames = useMemo(() => {
    return cn(
      valueContainerVariants({ borderless }),
      valueContainerStyleOverrides,
    )
  }, [borderless, valueContainerStyleOverrides, valueContainerVariants])

  const menuContainerClassNames = useMemo(() => {
    return cn(menuContainerVariants({ borderless }), menuStyleOverrides)
  }, [borderless, menuContainerVariants, menuStyleOverrides])

  const indicatorSeperatorClassNames = useMemo(() => {
    return cn(indicatorSeperatorVariants({ borderless }))
  }, [borderless, indicatorSeperatorVariants])

  const indicatorsContainerClassNames = useMemo(() => {
    return cn(
      indicatorsContainerVariants({ borderless }),
      indicatorsContainerStyleOverrides,
    )
  }, [
    borderless,
    indicatorsContainerVariants,
    indicatorsContainerStyleOverrides,
  ])

  const singleValueClassNames = useMemo(() => {
    return cn(singleValueVariants(), singleValueStyleOverrides)
  }, [singleValueVariants, singleValueStyleOverrides])

  const errorMessageId = `${inputId}-error-message`
    .toLowerCase()
    .replace(" ", "-")

  const SelectContainer =
    labelPosition === "top" || labelPosition === "bottom"
      ? ColumnContainer
      : RowContainer

  const CustomComponents = useMemo(() => {
    let customComponents: SelectComponentsConfig<T, IsMulti, Group> = {
      Option: (props: OptionProps<T, IsMulti, Group>) => {
        const { isSelected, isMulti, children, data } = props

        const isSentinel = isSentinelOption(data)
        const newInnerProps = {
          ...props.innerProps,
          "aria-selected": isSelected,
          ...(isSentinel ? { "aria-label": sentinelAriaLabel } : {}),
        }

        // Revamp shows a checkmark on the selected option for single-select
        // too; pre-revamp behavior was multi-only. Never show on the sentinel.
        const shouldShowCheckmark =
          !isSentinel && isSelected && (isMulti || revampOn)
        return (
          <components.Option {...props} innerProps={newInnerProps}>
            {shouldShowCheckmark ? (
              <div className="flex items-center -ml-2">
                <Icon iconType="whiteCheckmark" />
                <span className="ml-0.5">{children}</span>
              </div>
            ) : (
              children
            )}
          </components.Option>
        )
      },
    }
    if (typeof customOption === "function") {
      customComponents = {
        ...customComponents,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        MenuList: (props: any) => (
          <CustomDropDownMenu props={props} customOption={customOption} />
        ),
      }
    }
    if (borderless && !useDefaultBorderlessIndicator) {
      customComponents = {
        ...customComponents,
        DropdownIndicator: () => (
          <Icon iconType="chevronDown" size={iconSize} />
        ),
      }
    }
    if (revampOn) {
      // Indicator "|" separator mirrors the clear-all X visibility.
      customComponents = {
        ...customComponents,
        IndicatorSeparator: (props) => {
          const { hasValue, selectProps } = props
          if (!selectProps.isMulti || !hasValue) return null
          return <components.IndicatorSeparator {...props} />
        },
      }
    }
    return customComponents
  }, [
    borderless,
    customOption,
    useDefaultBorderlessIndicator,
    iconSize,
    revampOn,
    sentinelAriaLabel,
  ])

  if (readOnly) {
    // Find the selected value label
    let displayValue = ""
    if (isMulti && Array.isArray(value)) {
      displayValue = (value as T[]).map((opt) => opt.label).join(", ")
    } else if (isMulti && Array.isArray(defaultValue)) {
      displayValue = (defaultValue as T[]).map((opt) => opt.label).join(", ")
    } else if (value && typeof value === "object" && "label" in value) {
      displayValue = (value as T).label
    } else if (
      defaultValue &&
      typeof defaultValue === "object" &&
      "label" in defaultValue
    ) {
      displayValue = (defaultValue as T).label
    }
    return (
      <Input
        id={inputId}
        isRequired={isRequired}
        title={typeof label === "string" ? label : ""}
        displayTitle={showLabel}
        readOnly
        inputValue={displayValue}
        placeholder={typeof placeholder === "string" ? placeholder : ""}
        error={error}
        errorText={errorMessage}
        styleOverrides={singleValueStyleOverrides}
        // No-op for onInputChange
        onInputChange={() => {}}
        aria-label={ariaLabel}
        type={inputType}
      />
    )
  }
  if (!onChange) {
    return null
  }

  return (
    <SelectContainer styleOverrides={containerClassNames}>
      {typeof label === "string" ? (
        <Label
          size="sm"
          isRequired={isRequired}
          htmlFor={inputId}
          text={label}
          fontWeight="medium"
          styleOverrides={`${showLabel ? "" : "sr-only"}`}
        />
      ) : (
        label
      )}
      <ReactSelect
        aria-label={ariaLabel}
        aria-required={isRequired}
        classNames={{
          input: () => inputClassNames,
          control: ({ isFocused }) =>
            cn(
              controlVariants({ error, borderless, isFocused }),
              controlStyleOverrides,
            ),
          placeholder: () => placeholderClassNames,
          valueContainer: () => valueContainerClassNames,
          singleValue: () => singleValueClassNames,
          menu: () => menuContainerClassNames,
          option: ({ isSelected, isFocused }) =>
            cn(
              "minimum-touchtarget",
              optionVariants({ isSelected, isFocused, borderless }),
            ),
          indicatorSeparator: () => indicatorSeperatorClassNames,
          indicatorsContainer: () => indicatorsContainerClassNames,
        }}
        // DEV_NOTE: If you want to add a custom component override you must define it outside this component
        // https://react-select.com/components#defining-components
        components={CustomComponents}
        id={`${inputId}-select`}
        inputId={inputId}
        isClearable={effectiveIsClearable}
        isMulti={isMulti}
        onChange={effectiveOnChange}
        options={effectiveOptions}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder || ""}
        aria-errormessage={errorMessageId}
        aria-invalid={error}
        hideSelectedOptions={false}
        menuPlacement={menuPlacement}
        styles={{
          control: (base, state) => ({
            ...base,
            backgroundColor,
            cursor: "pointer",
            boxShadow: "0",
            border: "0",
            ...{
              ...(borderless &&
                state.isFocused && {
                  outline: "none",
                  boxShadow: "0 0 0 2px #2198CC, 0 0 0 4px white",
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: "0 0 0 2px #2198CC, 0 0 0 4px white",
                  },
                }),
            },
          }),
          dropdownIndicator: (base) => ({
            ...base,
            padding: "6px",
            color: dropdownIndicatorColor ?? "#6B7280",
            ...(dropdownIndicatorColor && {
              "&:hover": { color: dropdownIndicatorColor },
            }),
          }),
          clearIndicator: (base) => ({ ...base, color: "#6B7280" }),
          multiValueRemove: (base) => ({ ...base, color: "#6B7280" }),
          menu: (base) => ({ ...base, zIndex: ZIndex.selectMenu }),
          option: (base, state) => {
            if (!revampOn) return base
            const isHighlighted = state.isFocused && !state.isSelected
            const borderColor = isHighlighted
              ? REVAMP_TOKENS.gray200
              : "transparent"
            return {
              ...base,
              borderTop: `1px solid ${borderColor}`,
              borderBottom: `1px solid ${borderColor}`,
              ...(isHighlighted
                ? { backgroundColor: REVAMP_TOKENS.primaryBlueLight }
                : {}),
            }
          },
        }}
        menuPortalTarget={null}
        {...rest}
      />
      {error && errorMessage && (
        <span id={errorMessageId} role="alert" className="error-text mt-0">
          {errorMessage}
        </span>
      )}
    </SelectContainer>
  )
}

export default Select
